<section class="ai-agent-panel" [attr.aria-label]="title">
  <header
    class="ai-agent-panel__header"
    [class.ai-agent-panel__header--with-back]="view() === 'settings'"
    simFloatingWindowHandle
  >
    @if (view() === 'settings') {
      <sim-tooltip class="ai-agent-panel__action" [tooltip]="backToChatLabel" [focusable]="false">
        <button
          sim-icon-button
          simColor="ghost"
          type="button"
          id="ai-agent-panel-back"
          [attr.aria-label]="backToChatLabel"
          (click)="showChat()"
        >
          <sim-icon icon="back" [sizeInPx]="20" />
        </button>
      </sim-tooltip>
      <h2 class="ai-agent-panel__title" simEllipsisTooltip>{{ accessLabel }}</h2>
    } @else {
      <span class="ai-agent-panel__badge" aria-hidden="true">AI</span>
      <h2 class="ai-agent-panel__title" [class.ai-agent-panel__title--chat]="inConversation()" simEllipsisTooltip>
        {{ headerTitle() }}
      </h2>
      @if (!store.blockedState()) {
        <sim-tooltip class="ai-agent-panel__action" [tooltip]="newConversationLabel" [focusable]="false">
          <button
            sim-icon-button
            simColor="ghost"
            type="button"
            id="ai-agent-panel-new-conversation"
            [attr.aria-label]="newConversationLabel"
            [disabled]="store.sending()"
            (click)="onNewConversation()"
          >
            <sim-icon icon="add" [sizeInPx]="20" />
          </button>
        </sim-tooltip>
      }
      <!-- Not enabled: there is no history to read. -->
      @if (!blockedView()) {
        <sim-tooltip
          class="ai-agent-panel__action"
          [class.ai-agent-panel__action--active]="historyOpen()"
          [tooltip]="historyOpen() ? closeHistoryLabel : historyLabel"
          [focusable]="false"
        >
          <button
            sim-icon-button
            simColor="ghost"
            type="button"
            id="ai-agent-panel-conversations"
            [attr.aria-label]="historyOpen() ? closeHistoryLabel : historyLabel"
            [attr.aria-expanded]="historyOpen()"
            (click)="toggleHistory()"
          >
            <sim-icon icon="history_log" [sizeInPx]="20" />
          </button>
        </sim-tooltip>
      }
      <sim-tooltip class="ai-agent-panel__action" [tooltip]="accessLabel" [focusable]="false">
        <button
          sim-icon-button
          simColor="ghost"
          type="button"
          id="ai-agent-panel-settings"
          [attr.aria-label]="accessLabel"
          (click)="showSettings()"
        >
          <sim-icon icon="settings" [sizeInPx]="20" />
        </button>
      </sim-tooltip>
    }
    <sim-tooltip class="ai-agent-panel__action ai-agent-panel__action--end" [tooltip]="closeLabel" [focusable]="false">
      <button
        sim-icon-button
        simColor="ghost"
        type="button"
        id="ai-agent-panel-close"
        [attr.aria-label]="closeLabel"
        (click)="onClose()"
      >
        <sim-icon icon="cancel" [sizeInPx]="20" />
      </button>
    </sim-tooltip>
  </header>

  @if (view() === 'settings') {
    <ai-agent-access-settings
      class="ai-agent-panel__body"
      [status]="store.userStatus()"
      [state]="store.assistantState()"
      [contactEmail]="contactEmail"
      [userEmail]="userEmail()"
      [checkedAt]="store.userStatusCheckedAt()"
      [loading]="store.userStatusLoading()"
      (refresh)="onRefreshStatus()"
    />
  } @else if (blockedView()) {
    <!-- Not enabled: nothing to read or type, the plate replaces the chat. -->
    <ai-agent-access-state
      class="ai-agent-panel__body"
      [contactEmail]="contactEmail"
      [userEmail]="userEmail()"
      [checking]="store.userStatusLoading()"
      (checkAgain)="onRefreshStatus()"
    />
  } @else {
    <!-- Positioned container of the history popover, which dims everything below the header. -->
    <div class="ai-agent-panel__main">
      <ai-agent-message-list
        class="ai-agent-panel__body"
        [messages]="store.messages()"
        [spaceName]="spaceName()"
        [loading]="store.messagesLoading()"
        [sending]="store.sendingInSelected()"
        [cardsHint]="store.blockedState() === 'noTokens' ? cardsStillWorkLabel : null"
        (retry)="onRetry($event)"
        (action)="onAction($event)"
        (goToPosition)="onGoToPosition($event)"
      />

      <div class="ai-agent-panel__bottom">
        @if (store.error(); as error) {
          <div class="ai-agent-panel__error" role="alert">
            <sim-icon class="ai-agent-panel__error-icon" icon="error_outline" [sizeInPx]="18" />
            <div class="ai-agent-panel__error-texts">
              <span class="ai-agent-panel__error-title">{{ error.message }}</span>
              @if (error.detail) {
                <span class="ai-agent-panel__error-detail">{{ error.detail }}</span>
              }
            </div>
            <sim-tooltip [tooltip]="dismissLabel" [focusable]="false">
              <button
                type="button"
                id="ai-agent-panel-error-dismiss"
                class="ai-agent-panel__error-dismiss"
                [attr.aria-label]="dismissLabel"
                (click)="onDismissError()"
              >
                <sim-icon icon="close" [sizeInPx]="16" />
              </button>
            </sim-tooltip>
          </div>
        }
        @if (store.accessTexts(); as texts) {
          <!-- Out of tokens: conversations and their cards stay usable, only typing is blocked. -->
          <div class="ai-agent-panel__notice" role="status">
            <div class="ai-agent-panel__notice-head">
              <span class="ai-agent-panel__notice-icon">
                <sim-icon icon="error_outline" [sizeInPx]="18" />
              </span>
              <div class="ai-agent-panel__notice-texts">
                <span class="ai-agent-panel__notice-title">{{ texts.title }}</span>
                <span class="ai-agent-panel__notice-text">{{ texts.description }}</span>
              </div>
            </div>
            <div class="ai-agent-panel__notice-actions">
              <button type="button" class="ai-agent-panel__notice-button" (click)="showSettings()" i18n="@@AI_AGENT_SEE_USAGE">
                See usage
              </button>
              <a
                class="ai-agent-panel__notice-button ai-agent-panel__notice-button--primary"
                [href]="contactMailto()"
                i18n="@@AI_AGENT_EMAIL_SIMLAB"
                >Email Simlab</a
              >
            </div>
          </div>
        } @else {
          <ai-agent-message-composer
            [disabled]="!store.canSend()"
            [pendingMessage]="store.pendingMessage()"
            [restoredMessage]="store.restoredMessage()"
            (send)="onSend($event)"
            (restored)="onMessageRestored()"
          />
        }
        <footer class="ai-agent-panel__footer">
          <span class="ai-agent-panel__space">
            <sim-icon class="ai-agent-panel__footer-icon" icon="grid" [sizeInPx]="13" />
            <span class="ai-agent-panel__space-name" simEllipsisTooltip>
              <ng-container i18n="@@AI_AGENT_SPACE_LABEL">Space:</ng-container> {{ spaceName() }}
            </span>
          </span>
          @if (statusChip(); as chip) {
            <button
              type="button"
              id="ai-agent-panel-status"
              class="ai-agent-panel__status"
              [class.ai-agent-panel__status--blocked]="chip.blocked"
              [attr.aria-label]="accessLabel"
              (click)="showSettings()"
            >
              <sim-icon [icon]="chip.icon" [sizeInPx]="13" />
              {{ chip.label }}
            </button>
          }
        </footer>
      </div>

      @if (historyOpen()) {
        <sim-history-popover
          [title]="historyLabel"
          [groups]="historyGroups()"
          [currentId]="store.selectedConversationId()"
          [currentLabel]="currentLabel"
          [newItemLabel]="newConversationLabel"
          [emptyTitle]="noChatsTitle"
          [emptyText]="noChatsText"
          [emptyActionLabel]="startChatLabel"
          [busyLabel]="sendingLabel"
          (selected)="onSelectConversation($event)"
          (newItem)="onNewConversation()"
          (closed)="closeHistory()"
        />
      }
    </div>
  }
</section>
